import axios from "axios";
import { useAtom, useSetAtom } from "jotai";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { IWarehouse, IWarehouseInput, warehousesAtom, warehousesLoadingAtom } from "./types";

type UseWarehouses = [IWarehouse[], {
    list: (query?: Record<string, any>) => Promise<IWarehouse[] | null>;
    getById: (id: number) => Promise<IWarehouse | null>;
    getPaginated: (first?: number, offset?: number, query?: Record<string, any>) => Promise<IWarehouse[] | null>;
    create: (input: IWarehouseInput) => Promise<IWarehouse | null>;
    update: (id: number, input: IWarehouseInput) => Promise<IWarehouse | null>;
    remove: (id: number) => Promise<IWarehouse | null>;
}];

const gqlEndpoint = `${environment.baseUrl}/graphql`;
const graphqlRequest = async (authToken: string | null, query: string, variables?: any) => {
    const { data } = await axios.post(gqlEndpoint, { query, variables }, { headers: { 'X-Wallmates-Auth': authToken } });
    if (data.errors) throw new Error(data.errors[0]?.message || 'GraphQL error');
    return data.data;
}

export const useWarehouses = (): UseWarehouses => {
    const [items, setItems] = useAtom(warehousesAtom);
    const setLoading = useSetAtom(warehousesLoadingAtom);
    const [, setSnackbar] = useSnackbar();
    const [, authToken, loginFunctions] = useLogin();

    const list = async (query: Record<string, any> = {}) => {
        try { setLoading(true);
            const q = `query($query: JSON){ getWarehouses(query: $query){ id name description width height length } }`;
            const data = await graphqlRequest(authToken, q, { query });
            setItems(data.getWarehouses);
            return data.getWarehouses as IWarehouse[];
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); setSnackbar({show:true,snackbarLevel:'error',text:'Failed to fetch warehouses.'}); return null; }
        finally { setLoading(false);} };

    const getById = async (id: number) => {
        try { const q = `query($id: ID!){ getWarehouseById(id:$id){ id name description width height length } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.getWarehouseById as IWarehouse;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const getPaginated = async (first = 20, offset = 0, query: Record<string, any> = {}) => {
        try { const q = `query($first:Int,$offset:Int,$query:JSON){ getPaginatedWarehouses(first:$first,offset:$offset,query:$query){ id name description width height length } }`;
            const data = await graphqlRequest(authToken, q, { first, offset, query });
            return data.getPaginatedWarehouses as IWarehouse[];
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const create = async (input: IWarehouseInput) => {
        try { const q = `mutation($input:WarehouseInput!){ createWarehouse(input:$input){ id } }`;
            const data = await graphqlRequest(authToken, q, { input });
            return data.createWarehouse as IWarehouse;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const update = async (id: number, input: IWarehouseInput) => {
        try { const q = `mutation($id:ID!,$input:WarehouseInput!){ updateWarehouse(id:$id,input:$input){ id } }`;
            const data = await graphqlRequest(authToken, q, { id, input });
            return data.updateWarehouse as IWarehouse;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const remove = async (id: number) => {
        try { const q = `mutation($id:ID!){ deleteWarehouse(id:$id){ id } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.deleteWarehouse as IWarehouse;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    return [items, { list, getById, getPaginated, create, update, remove }];
}


